import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { FleetSegmentCalculationStatus } from 'src/app/shared/enums/fleet-segment-calculation-status.enum';
import { DealerInformation } from 'src/app/shared/models/dealer-information.model';
import { FleetLoadResult } from 'src/app/shared/models/fleet-load-result.model';

import { DealerStore } from '../../stores/dealer.store';
import { CaseCreationStore } from '../../stores/v2/case-creation.store';
import { VehicleService } from '../vehicle.service';
import { GridManagerService } from './grid-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { DsToastModel, DsToastService } from '@bmw-ds/components';
import { BaseTone } from 'src/app/shared/enums/tone.enum';
import { Exception } from 'src/app/shared/models/exception.model';

@Injectable()
export class FleetService {
  private readonly caseCreationStore = inject(CaseCreationStore);
  private readonly dealerStore = inject(DealerStore);
  private readonly gridManagerService = inject(GridManagerService);
  private readonly vehicleService = inject(VehicleService);
  private readonly translationService = inject(TranslateService);
  private readonly toastService = inject(DsToastService);

  readonly partialErrorCreatingFleet = signal<boolean>(false);
  readonly fleetUpdating = signal<boolean>(false);
  readonly isFleetFromVdc = signal<boolean>(false);

  loadDealerFleetInformation(destroy: DestroyRef): void {
    const dealer = this.dealerStore.selectedDealer();

    if (!dealer) {
      return;
    }

    this.gridManagerService.startLoading();
    this.fleetUpdating.set(false);
    this.isFleetFromVdc.set(!!dealer.vdc);

    this.vehicleService
      .getFleetStatus(dealer.bpId)
      .pipe(
        takeUntilDestroyed(destroy),
        switchMap((allStatus): Observable<FleetLoadResult> => {
          if (allStatus.length === 0) {
            // fleet doesn't exist in our DB
            this.fleetUpdating.set(true);
            return this.createAndLoadFleet(dealer, destroy);
          }

          const allReady = allStatus.every((status) => status === FleetSegmentCalculationStatus.READY);

          if (allReady) {
            return this.requestFleetInformation(dealer.bpId, destroy); // only makes sense to call SVDS if all fleet segments are ready
          }

          this.fleetUpdating.set(true);
          return of({ status: 'updating' as const });
        }),
        catchError((error) => {
          console.error('Error getting fleet status for bpId:', dealer.bpId, error);
          return of({ status: 'error' as const });
        }),
        finalize(() => this.gridManagerService.stopLoading())
      )
      .subscribe((result) => {
        switch (result.status) {
          case 'ready':
            this.fleetUpdating.set(false);
            this.caseCreationStore.setSvdsInformation(result.vehicles);
            if (this.dealerStore.isValidationDealer()) {
              const vins = result.vehicles.map((v) => v.vin);
              this.caseCreationStore.initializeValidationCampaignVins(vins, true);
            }
            break;
          case 'updating':
          case 'error':
            break;
        }
      });
  }

  private createAndLoadFleet(dealer: DealerInformation, destroy: DestroyRef): Observable<FleetLoadResult> {
    return this.createDealerFleet(dealer).pipe(
      switchMap((result) => {
        if (result.success) {
          return this.requestFleetInformation(dealer.bpId, destroy);
        }
        return of({ status: 'error' as const });
      }),
      catchError((error) => {
        const exception = new Exception(error.error);
        if (exception.options['pureCode']) {
          const errorMessage = this.translationService.instant('dashboard.errors.' + exception.options['pureCode']);
          this.sendToast('requesting-fleet-information-error-toast', BaseTone.CRITICAL, errorMessage);
        }
        return of({ status: 'error' as const });
      })
    );
  }

  private createDealerFleet(dealer: DealerInformation): Observable<{ success: boolean }> {
    this.partialErrorCreatingFleet.set(false);
    return !dealer.vdc
      ? this.vehicleService.createDealerFleet(dealer.bpId).pipe(
          map(() => ({ success: true })),
          catchError((error) => {
            this.partialErrorCreatingFleet.set(true);
            this.fleetUpdating.set(false);
            const exception = new Exception(error.error);
            if (exception.options['pureCode']) {
              const errorMessage = this.translationService.instant('dashboard.errors.' + exception.options['pureCode']);
              this.sendToast('requesting-fleet-information-error-toast', BaseTone.CRITICAL, errorMessage);
            }
            return of({ success: false });
          })
        )
      : of({ success: true });
  }

  private requestFleetInformation(bpId: string, destroy: DestroyRef): Observable<FleetLoadResult> {
    return this.vehicleService.getDealerFleet(bpId).pipe(
      takeUntilDestroyed(destroy),
      map((vehicles) => ({ status: 'ready' as const, vehicles })),
      catchError((error) => {
        const exception = new Exception(error.error);
        if (exception.options['pureCode']) {
          const errorMessage = this.translationService.instant('dashboard.errors.' + exception.options['pureCode']);
          this.sendToast('requesting-fleet-information-error-toast', BaseTone.CRITICAL, errorMessage);
        }
        return of({ status: 'error' as const });
      })
    );
  }

  private sendToast(id: string, tone: BaseTone, toastText: string): void {
    this.toastService.pushToast({
      id,
      tone,
      toastText,
    } as DsToastModel);
  }
}
